import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, RefreshCw, Phone, ChevronUp, ChevronDown, ArrowUpDown, Inbox,
} from 'lucide-react';
import { getRichieste, countRichieste, countRichiesteDerivato, type RichiestaListItem } from '../api/richieste';
import Badge from '../components/ui/Badge';
import NotaInternaRiga from '../components/richieste/NotaInternaRiga';
import StatusBadge from '../components/ui/StatusBadge';
import { SkeletonRow } from '../components/ui/Skeleton';
import { useOrdiniCountStore } from '../store/ordiniStore';
import { useCategorie } from '../hooks/useCategorie';
import { categoriaLabel, categoriaVariant } from '../lib/categorie';

// ── Tabs ─────────────────────────────────────────────────────────────────────

// Tab fase Ordine — allineato a order_status_v2 (#95) + stati derivati (#97).
// Enum: in_progress/concluso/annullato (filtro ?status=). Derivati: fatturato/pagato
// (filtro ?fatturazione=, calcolati lato BE su ordini conclusi).
const ENUM_TABS = [
  { key: 'in_progress', label: 'conferimenti.ordini.tab_in_progress' },
  { key: 'concluso', label: 'conferimenti.ordini.tab_concluso' },
  { key: 'annullato', label: 'conferimenti.ordini.tab_annullato' },
] as const;
const DERIVED_TABS = [
  { key: 'fatturato', label: 'conferimenti.ordini.tab_fatturato' },
  { key: 'pagato', label: 'conferimenti.ordini.tab_pagato' },
] as const;

const ENUM_KEYS = ENUM_TABS.map((tab) => tab.key) as readonly string[];
function isDerivedKey(k: string): k is 'fatturato' | 'pagato' {
  return k === 'fatturato' || k === 'pagato';
}
const ALL_TABS = [...ENUM_TABS, ...DERIVED_TABS];

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDateIT(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

type SortDir = 'asc' | 'desc';
type SortKey = 'codice_ordine' | 'customer' | 'categoria' | 'days_pending' | 'created_at';

function SortIcon({ col, sortBy, sortDir }: { col: string; sortBy: SortKey; sortDir: SortDir }) {
  if (sortBy !== col) return <ArrowUpDown size={13} className="text-ink-faint" />;
  return sortDir === 'asc' ? <ChevronUp size={13} className="text-sage" /> : <ChevronDown size={13} className="text-sage" />;
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function OrdiniPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeKey = searchParams.get('status') ?? 'in_progress';

  const [ordini, setOrdini] = useState<RichiestaListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});

  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('days_pending');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const refetchOrdiniCount = useOrdiniCountStore((s) => s.refetch);
  const { categorie: categorieList } = useCategorie();

  const fetchOrdini = useCallback(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const filters = {
          ...(isDerivedKey(activeKey) ? { fatturazione: activeKey } : { status: activeKey }),
          ...(categoria && { categoria }),
        };
        const data = await getRichieste(filters);
        setOrdini(data);
      } catch {
        setError(t('conferimenti.ordini.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [activeKey, categoria, t]);

  const fetchCounts = useCallback(() => {
    void (async () => {
      // Contatori per TUTTI i tab: enum via ?status= (concluso ora esclusivo lato BE),
      // derivati via ?fatturazione= (fatturato/pagato mutuamente esclusivi lato BE).
      const [enumCounts, derivedCounts] = await Promise.all([
        Promise.allSettled(ENUM_TABS.map((tab) => countRichieste(tab.key))),
        Promise.allSettled(DERIVED_TABS.map((tab) => countRichiesteDerivato(tab.key))),
      ]);
      const map: Record<string, number> = {};
      ENUM_TABS.forEach((tab, i) => {
        const r = enumCounts[i];
        map[tab.key] = r.status === 'fulfilled' ? r.value : 0;
      });
      DERIVED_TABS.forEach((tab, i) => {
        const r = derivedCounts[i];
        map[tab.key] = r.status === 'fulfilled' ? r.value : 0;
      });
      setTabCounts(map);
    })();
  }, []);

  useEffect(() => { fetchOrdini(); }, [fetchOrdini]);
  useEffect(() => { fetchCounts(); refetchOrdiniCount(); }, [fetchCounts, refetchOrdiniCount]);

  const filtered = useMemo(() => {
    let list = [...ordini];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.codice_ordine.toLowerCase().includes(q) ||
          r.customer.ragione_sociale.toLowerCase().includes(q) ||
          (r.referente_richiesta ?? '').toLowerCase().includes(q),
      );
    }
    list.sort((a, b) => {
      let av: string | number;
      let bv: string | number;
      switch (sortBy) {
        case 'customer': av = a.customer.ragione_sociale; bv = b.customer.ragione_sociale; break;
        case 'days_pending': av = a.days_pending; bv = b.days_pending; break;
        case 'created_at': av = a.created_at; bv = b.created_at; break;
        default: av = a.codice_ordine; bv = b.codice_ordine;
      }
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [ordini, search, sortBy, sortDir]);

  const handleSort = (col: SortKey) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  };

  const handleTabChange = (s: string) => {
    setSearchParams({ status: s });
    setSearch('');
    setCategoria('');
  };

  const activeTab = ALL_TABS.find((tab) => tab.key === activeKey);
  const activeTabLabel = activeTab ? t(activeTab.label) : activeKey;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink-dark tracking-tight mb-1">{t('conferimenti.ordini.titolo')}</h1>
          <span className="text-sm text-ink-soft">
            <strong className="text-ink-dark font-semibold">{ordini.length}</strong>{' '}{activeTabLabel.toLowerCase()}
          </span>
        </div>
        <button
          type="button"
          onClick={() => { fetchOrdini(); fetchCounts(); }}
          disabled={loading}
          className="flex items-center gap-2 px-3.5 py-2 bg-white border border-stone-border rounded-lg text-sm text-ink-mid hover:bg-stone-bg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          <span>{t('conferimenti.ordini.aggiorna')}</span>
        </button>
      </div>

      {/* Tab stati */}
      <div className="flex gap-0.5 overflow-x-auto mb-4 border-b border-stone-mid">
        {ALL_TABS.map((tab) => {
          const isActive = tab.key === activeKey;
          const count = tabCounts[tab.key];
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabChange(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${isActive ? 'border-sage text-sage bg-sage-light/30' : 'border-transparent text-ink-mid hover:text-ink-dark hover:border-stone-border'}`}
            >
              {ENUM_KEYS.includes(tab.key)
                ? <StatusBadge status={tab.key} size="sm" />
                : <span>{t(tab.label)}</span>}
              {count != null && count > 0 && (
                <span className={`min-w-[20px] h-5 px-1 text-[11px] font-semibold rounded-full flex items-center justify-center ${isActive ? 'bg-sage text-white' : 'bg-stone-mid text-ink-mid'}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filtri */}
      <div className="bg-white border border-stone-mid rounded-xl px-4 py-3 mb-4 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('conferimenti.ordini.cerca_placeholder')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-border rounded-md focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage text-ink-dark"
          />
        </div>
        <select
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
          className="px-3 py-2 text-sm border border-stone-border rounded-md bg-white text-ink-dark focus:outline-none focus:ring-2 focus:ring-sage"
        >
          <option value="">{t('conferimenti.ordini.tutte_categorie')}</option>
          {categorieList.map((c) => <option key={c.codice} value={c.codice}>{c.nome_visualizzato}</option>)}
        </select>
        {(search || categoria) && (
          <button type="button" onClick={() => { setSearch(''); setCategoria(''); }} className="text-sm text-ink-soft hover:text-ink-dark">
            {t('conferimenti.ordini.pulisci_filtri')}
          </button>
        )}
      </div>

      {/* Contenuto */}
      {error ? (
        <div className="bg-accent-danger-bg border border-[#FECACA] rounded-xl p-6 text-center">
          <p className="text-accent-danger font-medium mb-3">{error}</p>
          <button type="button" onClick={fetchOrdini} className="text-sm text-sage underline">{t('conferimenti.ordini.riprova')}</button>
        </div>
      ) : filtered.length === 0 && !loading ? (
        <div className="bg-white border border-stone-mid rounded-xl p-12 text-center">
          <Inbox size={40} className="text-ink-faint mx-auto mb-3" />
          <p className="text-ink-mid font-medium">{t('conferimenti.ordini.vuoto', { tab: activeTabLabel.toLowerCase() })}</p>
          {(search || categoria) && <p className="text-ink-soft text-sm mt-1">{t('conferimenti.ordini.modifica_filtri')}</p>}
        </div>
      ) : (
        <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-mid bg-stone-bg">
                {([
                  { key: 'codice_ordine', label: t('conferimenti.ordini.col_codice') },
                  { key: 'customer', label: t('conferimenti.ordini.col_cliente') },
                  { key: 'categoria', label: t('conferimenti.ordini.col_categoria') },
                  { key: 'days_pending', label: t('conferimenti.ordini.col_giorni') },
                  { key: null, label: t('conferimenti.ordini.col_referente') },
                  { key: null, label: '' },
                ] as { key: SortKey | null; label: string }[]).map(({ key, label }, i) => (
                  <th
                    key={i}
                    className={`px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide ${key ? 'cursor-pointer hover:text-ink-dark select-none' : ''}`}
                    onClick={key ? () => handleSort(key) : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {label}
                      {key && <SortIcon col={key} sortBy={sortBy} sortDir={sortDir} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={6} />)
                : filtered.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b border-stone-mid last:border-0 hover:bg-stone-bg transition-colors cursor-pointer"
                      onClick={() => navigate(`/ordini/${r.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs font-semibold text-sage">{r.codice_ordine}</div>
                        {r.pagato_ordine ? (
                          <span className="mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-sage-light text-sage-dark">{t('conferimenti.ordini.pill_pagato')}</span>
                        ) : r.fatturato_ordine ? (
                          <span className="mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700">{t('conferimenti.ordini.pill_fatturato')}</span>
                        ) : r.status === 'concluso' ? (
                          <span className="mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-stone-bg text-ink-soft">{t('conferimenti.ordini.pill_concluso')}</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-ink-dark font-medium max-w-[260px]">
                        <span className="block truncate">{r.customer.ragione_sociale}</span>
                        <NotaInternaRiga nota={r.nota_interna} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={categoriaVariant(r.categoria)}>{categoriaLabel(r.categoria, categorieList)}</Badge>
                      </td>
                      <td className="px-4 py-3 text-ink-mid">
                        <div className="text-xs">{r.days_pending === 0 ? t('conferimenti.ordini.oggi') : r.days_pending === 1 ? t('conferimenti.ordini.ieri') : t('conferimenti.ordini.giorni_fa', { n: r.days_pending })}</div>
                        <div className="text-[11px] text-ink-faint">{formatDateIT(r.created_at)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-ink-dark">{r.referente_richiesta}</div>
                        <a href={`tel:${r.telefono_richiesta}`} onClick={(e) => e.stopPropagation()}
                          className="text-[11px] text-sage hover:underline flex items-center gap-1">
                          <Phone size={11} />{r.telefono_richiesta}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/ordini/${r.id}`); }}
                          className="px-3 py-1.5 text-xs font-medium text-sage border border-sage rounded-md hover:bg-sage-light transition-colors">
                          {t('conferimenti.ordini.apri')}
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
          {!loading && filtered.length > 0 && (
            <div className="px-4 py-2.5 border-t border-stone-mid bg-stone-bg text-xs text-ink-soft">
              {filtered.length !== ordini.length
                ? t('conferimenti.ordini.footer_di', { n: filtered.length, total: ordini.length })
                : t('conferimenti.ordini.footer', { n: filtered.length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
